/**
 * The topbar trail: which group a screen belongs to, then the screen itself.
 *
 * Derived from the same nav model the sidebar uses, so a tool can never appear
 * under one heading in the rail and another in the trail. Deep links
 * (`/matters/:id`, `/documents/:id`) resolve to their list page plus a leaf
 * label from `lib/seo.ts`.
 */
import { Link, useLocation } from 'react-router-dom';
import { pageTitle } from '@/lib/seo';
import { ADMIN_NAV, TOOL_GROUPS, UTILITY_NAV } from './nav';

interface Crumb {
  label: string;
  to?: string;
}

export function crumbsFor(pathname: string): Crumb[] {
  const title = pageTitle(pathname) ?? 'ALFIE';

  // An exact nav match gives us the group heading for free.
  for (const group of TOOL_GROUPS) {
    for (const item of group.items) {
      if (item.to === pathname) return [{ label: group.label }, { label: item.label }];
    }
  }
  if (ADMIN_NAV.some((i) => i.to === pathname)) return [{ label: 'Administration' }, { label: title }];
  if (UTILITY_NAV.some((i) => i.to === pathname)) return [{ label: title }];

  // A detail route: link back to the list it belongs to.
  const parent = [...TOOL_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })))]
    .filter((i) => i.to !== '/' && pathname.startsWith(`${i.to}/`))
    // Longest prefix wins, so /valorem/runs/:id resolves to /valorem, not /.
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (parent) return [{ label: parent.group }, { label: parent.label, to: parent.to }, { label: title }];

  return [{ label: title }];
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const crumbs = crumbsFor(pathname);

  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={`${c.label}-${i}`} className="row gap-1 truncate">
            {i > 0 && <span className="sep" aria-hidden>/</span>}
            {c.to && !last ? (
              <Link to={c.to}>{c.label}</Link>
            ) : (
              <span className={last ? 'current' : undefined} aria-current={last ? 'page' : undefined}>
                {c.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
